import React from "react";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Coupon } from "../../../models/inventory/Coupon";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import { SortType } from "../../../models/SortType";
import CustomTypography from "../common/form/CustomTypography";
import { formatDate } from "../common/functions/formatDate";

interface CouponsTableProp {
  maxHeight: number;
  coupons: Coupon[];
  allSelected: boolean;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  selectedShipmentIdsMap: Map<number, number>;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  selectAllItem: () => void;
  selectItem: (coupon: Coupon) => void;
  deSelectAllItem: () => void;
  deSelectItem: (coupon: Coupon) => void;
  onViewDetails: (coupon: Coupon) => void;
}

const getCouponStatus = (coupon: Coupon) => {
  if (coupon.reactivated) return "Reactivated";
  if (coupon.expired) return "Expired";
  if (coupon.redeemed) return "Redeemed";
  if (coupon.opened) return "Opened";
  if (coupon.sent) return "Sent";
  return "Not Sent";
};

const getCouponStatusChip = (coupon: Coupon) => {
  const status = getCouponStatus(coupon);
  let color: "success" | "error" | "warning" | "info" | "default" = "default";

  if (coupon.reactivated) {
    color = "success";
  } else if (coupon.expired) {
    color = "error";
  } else if (coupon.redeemed) {
    color = "success";
  } else if (coupon.opened) {
    color = "info";
  } else if (coupon.sent) {
    color = "info";
  } else {
    color = "default";
  }

  return <Chip label={status} size="small" color={color} variant="outlined" />;
};

const CouponsTable = ({
  maxHeight,
  coupons,
  allSelected,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  selectedShipmentIdsMap,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  selectAllItem,
  selectItem,
  deSelectAllItem,
  deSelectItem,
  onViewDetails,
}: CouponsTableProp) => {
  const theme = useTheme<Theme>();

  const areSomeSelected = () =>
    selectedShipmentIdsMap.size > 0 &&
    selectedShipmentIdsMap.size !== coupons.length;

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const renderRecipient = (coupon: Coupon) => (
    <Box>
      <CustomTypography variant="body2" fontWeight={600}>
        {coupon.user_first_name || coupon.first_name || "-"}{" "}
        {coupon.user_last_name || coupon.last_name || ""}
      </CustomTypography>
      <CustomTypography variant="body2" color="text.secondary">
        {coupon.user_email || coupon.email || "-"}
      </CustomTypography>
    </Box>
  );

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {coupons.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      textAlign: "center",
                      fontSize: 14,
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                      minWidth: "60px",
                    }}
                  >
                    <Checkbox
                      color="primary"
                      indeterminate={areSomeSelected()}
                      checked={allSelected}
                      onClick={() => {
                        allSelected ? deSelectAllItem() : selectAllItem();
                      }}
                    />
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 60,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    title="Recipient"
                    fontSize="14"
                    minWidth="220px"
                  />
                  <AppTableHeaderCell
                    title="Order Id"
                    fontSize="14"
                    minWidth="100px"
                  />
                  <AppTableHeaderCell title="Template" fontSize="14" />
                  <AppTableHeaderCell title="Customer" fontSize="14" />
                  <AppTableHeaderCell title="Status" fontSize="14" />
                  <AppTableHeaderCell fontSize="14">
                    <TableSortLabel
                      active={sortBy === "sent_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "sent_date",
                          sortBy === "sent_date" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("sent_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Sent Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="14">
                    <TableSortLabel
                      active={sortBy === "opened_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "opened_date",
                          sortBy === "opened_date" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("opened_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Opened Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Redeemed Date" fontSize="14">
                    <TableSortLabel
                      active={sortBy === "redeemed_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "redeemed_date",
                          sortBy === "redeemed_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("redeemed_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Redeemed Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Expiry Date" fontSize="14">
                    <TableSortLabel
                      active={sortBy === "expiry_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "expiry_date",
                          sortBy === "expiry_date" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC,
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("expiry_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Expiry Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Reactivated Date" fontSize="14" />

                  <AppTableHeaderCell
                    title="Selected Pack"
                    fontSize="14"
                    minWidth="140px"
                  />
                  <AppTableHeaderCell title="Reminders" fontSize="14" />
                  <AppTableHeaderCell
                    title="Last Reminder Date"
                    fontSize="14"
                  />
                  <AppTableHeaderCell title="Created Date" fontSize="14" />
                  <AppTableHeaderCell title="Modified Date" fontSize="14" />
                  <AppTableHeaderCell
                    title="Actions"
                    fontSize="14"
                    minWidth="90px"
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {coupons.map((coupon) => {
                  const isSelected = selectedShipmentIdsMap.has(coupon.id);
                  return (
                    <TableRow
                      key={coupon.id}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell
                        padding="checkbox"
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 15,
                          backgroundColor: theme.palette.background.paper,
                          textAlign: "center",
                          minWidth: "60px",
                        }}
                      >
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          onClick={() => {
                            isSelected
                              ? deSelectItem(coupon)
                              : selectItem(coupon);
                          }}
                        />
                      </TableCell>
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          left: 60,
                          position: "sticky",
                          zIndex: 15,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 600,
                          color:
                            theme.palette.customColors?.greyTextColor ||
                            theme.palette.text.secondary,
                        }}
                      >
                        {coupon.id}
                      </AppTableRowCell>
                      <AppTableRowCell sx={{ minWidth: 220 }}>
                        {renderRecipient(coupon)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {coupon.order_id || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {coupon.template_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {coupon.customer_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {getCouponStatusChip(coupon)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(coupon.sent_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(coupon.opened_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(coupon.redeemed_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(coupon.expiry_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(coupon.reactivation_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {coupon.pack_names || coupon.variants_in_str || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {coupon.reminders_sent ?? "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(coupon.last_reminder_sent_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(coupon.created_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(coupon.modified_date)}
                      </AppTableRowCell>
                      <AppTableRowCell sx={{ textAlign: "center" }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => onViewDetails(coupon)}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CouponsTable;
